import { useCallback, useEffect, useRef, useState } from "react";

type ChallengeState =
  | { name: "checking" }
  | { name: "interactive_challenge"; requestToken: string; siteKey: string }
  | { name: "retryable_outage"; requestToken: string }
  | { name: "expired_or_stale" }
  | { name: "access_denied" }
  | { name: "redirect_failed"; exchangeCode: string; exchangeUrl: string };

interface TurnstileApi {
  render(container: HTMLElement, options: {
    sitekey: string;
    action: string;
    callback(token: string): void;
    "error-callback"(): void;
    "expired-callback"(): void;
  }): string;
  remove(widgetId: string): void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

export function PublicAppChallengePage() {
  const requestToken = useRef(fragmentRequestToken());
  const [state, setState] = useState<ChallengeState>({ name: "checking" });

  const check = useCallback(async () => {
    const token = requestToken.current;
    if (!token) {
      setState({ name: "expired_or_stale" });
      return;
    }
    setState({ name: "checking" });
    try {
      const response = await postJson("/api/public-access/v1/context", { requestToken: token });
      if (!response.ok) {
        setState(response.status >= 500
          ? { name: "retryable_outage", requestToken: token }
          : response.status === 401
            ? { name: "expired_or_stale" }
            : { name: "access_denied" });
        return;
      }
      const payload = await response.json() as unknown;
      if (!isRecord(payload) || typeof payload.siteKey !== "string") {
        setState({ name: "access_denied" });
        return;
      }
      setState({ name: "interactive_challenge", requestToken: token, siteKey: payload.siteKey });
    } catch {
      setState({ name: "retryable_outage", requestToken: token });
    }
  }, []);

  useEffect(() => {
    clearFragment();
    void check();
  }, [check]);

  const returnToApp = state.name === "redirect_failed"
    ? safeAppOrigin(state.exchangeUrl)
    : null;

  return (
    <div className="app-shell agent-setup-shell">
      <header className="site-header">
        <a className="wordmark" href="/" aria-label="Doorway home">
          <span className="wordmark-icon" aria-hidden="true"><span /></span>
          Doorway
        </a>
        <span className="demo-pill">Visitor check</span>
      </header>
      <main className="workspace-gate">
        <section className="flow-card agent-setup-card" aria-live="polite" aria-busy={state.name === "checking"}>
          {state.name === "checking" && <><h1>Checking this visit</h1><p>Doorway is confirming that this request can safely open the app.</p></>}
          {state.name === "interactive_challenge" && (
            <InteractiveChallenge
              state={state}
              onOutage={() => setState({ name: "retryable_outage", requestToken: state.requestToken })}
              onExpired={() => setState({ name: "expired_or_stale" })}
              onDenied={() => setState({ name: "access_denied" })}
              onExchange={(exchangeCode, exchangeUrl) => {
                if (!submitExchange(exchangeCode, exchangeUrl)) {
                  setState({ name: "redirect_failed", exchangeCode, exchangeUrl });
                }
              }}
            />
          )}
          {state.name === "retryable_outage" && <><h1>The visitor check is temporarily unavailable</h1><p>No access was granted. Try again when you are ready.</p><button className="primary-button" type="button" onClick={() => void check()}>Try again</button></>}
          {state.name === "expired_or_stale" && <><h1>This visitor check expired</h1><p>Return to the app and open it again to start a fresh check.</p><a className="primary-button" href="/">Go to Doorway</a></>}
          {state.name === "access_denied" && <><h1>Access could not be granted</h1><p>Doorway could not safely continue this visit.</p><a className="primary-button" href="/">Go to Doorway</a></>}
          {state.name === "redirect_failed" && <><h1>Doorway could not return to the app</h1><p>Your check succeeded, but the browser did not finish the return step.</p><button className="primary-button" type="button" onClick={() => {
            if (!submitExchange(state.exchangeCode, state.exchangeUrl)) {
              setState({ ...state });
            }
          }}>Return to the app</button>{returnToApp && <a className="secondary-button" href={returnToApp}>Cancel</a>}</>}
        </section>
      </main>
    </div>
  );
}

function InteractiveChallenge({
  state,
  onOutage,
  onExpired,
  onDenied,
  onExchange,
}: {
  state: Extract<ChallengeState, { name: "interactive_challenge" }>;
  onOutage(): void;
  onExpired(): void;
  onDenied(): void;
  onExchange(code: string, url: string): void;
}) {
  const container = useRef<HTMLDivElement>(null);
  useEffect(() => {
    let active = true;
    let widgetId: string | null = null;
    let script: HTMLScriptElement | null = null;
    let ownsScript = false;
    const render = () => {
      if (!active || !container.current || !window.turnstile || widgetId) return;
      widgetId = window.turnstile.render(container.current, {
        sitekey: state.siteKey,
        action: "public_app_access",
        callback: (turnstileToken) => {
          void postJson("/api/public-access/v1/verify", {
            requestToken: state.requestToken,
            turnstileToken,
          }).then(async (response) => {
            if (!active) return;
            if (!response.ok) {
              if (response.status >= 500) onOutage();
              else if (response.status === 401) onExpired();
              else onDenied();
              return;
            }
            const payload = await response.json() as unknown;
            if (
              !isRecord(payload)
              || typeof payload.exchangeCode !== "string"
              || typeof payload.exchangeUrl !== "string"
              || !safeExchangeUrl(payload.exchangeUrl)
            ) {
              onDenied();
              return;
            }
            onExchange(payload.exchangeCode, payload.exchangeUrl);
          }).catch(onOutage);
        },
        "error-callback": onOutage,
        "expired-callback": onExpired,
      });
    };
    if (window.turnstile) render();
    else {
      const existing = document.querySelector<HTMLScriptElement>('script[data-doorway-turnstile="true"]');
      script = existing ?? document.createElement("script");
      if (!existing) {
        ownsScript = true;
        script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
        script.async = true;
        script.defer = true;
        script.dataset.doorwayTurnstile = "true";
        document.head.append(script);
      }
      script.addEventListener("load", render, { once: true });
      script.addEventListener("error", handleScriptError, { once: true });
    }
    function handleScriptError() {
      script?.removeEventListener("load", render);
      script?.removeEventListener("error", handleScriptError);
      script?.remove();
      if (active) onOutage();
    }
    return () => {
      active = false;
      script?.removeEventListener("load", render);
      script?.removeEventListener("error", handleScriptError);
      if (ownsScript && !window.turnstile) script?.remove();
      if (widgetId && window.turnstile) window.turnstile.remove(widgetId);
    };
  }, [onDenied, onExchange, onExpired, onOutage, state.requestToken, state.siteKey]);
  return <><h1>Confirm you are a person</h1><p>Complete this check to open the app. Doorway does not share app credentials with your browser.</p><div ref={container} aria-label="Human verification" /></>;
}

function fragmentRequestToken(): string | null {
  const fragment = new URLSearchParams(window.location.hash.replace(/^#/, ""));
  const values = fragment.getAll("request");
  return values.length === 1 && /^[A-Za-z0-9_-]{20,2048}$/.test(values[0]!) ? values[0]! : null;
}

function clearFragment(): void {
  if (!window.location.hash) return;
  window.history.replaceState(window.history.state, "", `${window.location.pathname}${window.location.search}`);
}

function postJson(path: string, body: Record<string, string>): Promise<Response> {
  return fetch(path, {
    method: "POST",
    credentials: "same-origin",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body),
    referrerPolicy: "no-referrer",
  });
}

function safeExchangeUrl(value: string): boolean {
  try {
    const url = new URL(value);
    return url.protocol === "https:"
      && !url.port
      && !url.username
      && !url.password
      && url.pathname === "/.platform/public-access/exchange"
      && !url.search
      && !url.hash;
  } catch {
    return false;
  }
}

function safeAppOrigin(exchangeUrl: string): string | null {
  return safeExchangeUrl(exchangeUrl) ? new URL(exchangeUrl).origin : null;
}

function submitExchange(code: string, exchangeUrl: string): boolean {
  if (!/^[A-Za-z0-9_-]{20,2048}$/.test(code) || !safeExchangeUrl(exchangeUrl)) return false;
  try {
    const form = document.createElement("form");
    form.method = "POST";
    form.action = exchangeUrl;
    const input = document.createElement("input");
    input.type = "hidden";
    input.name = "code";
    input.value = code;
    form.append(input);
    document.body.append(form);
    form.submit();
    return true;
  } catch {
    return false;
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
